<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>静态路由组</strong>
        <small class="text-muted ml-2">模板 + 批量同步到多台路由器（含 address-list 双身份）</small>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="primary" size="sm" @click="openModal()">新建组</CButton>
            <CButton color="secondary" size="sm" :disabled="loading" @click="loadList">刷新</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <CDataTable
          :items="groups"
          :fields="fields"
          hover
          striped
          :loading="loading"
          pagination
          :items-per-page="15"
        >
          <template #name="{ item }">
            <td class="align-middle">
              <CLink @click="goDetail(item.id)">{{ item.name }}</CLink>
              <div v-if="item.description"><small class="text-muted">{{ item.description }}</small></div>
            </td>
          </template>
          <template #family="{ item }">
            <td class="align-middle">
              <CBadge :color="item.family === 'ipv6' ? 'dark' : 'light'">{{ item.family }}</CBadge>
              <CBadge :color="item.route_type === 'blackhole' ? 'danger' : 'success'" class="ml-1">{{ item.route_type }}</CBadge>
              <small v-if="item.distance !== null && item.distance !== undefined" class="text-muted ml-1">dist {{ item.distance }}</small>
            </td>
          </template>
          <template #alist="{ item }">
            <td class="align-middle">
              <template v-if="item.as_address_list">
                <CBadge color="info">alist</CBadge>
                <small class="ml-1">{{ item.list_name }}</small>
              </template>
              <span v-else class="text-muted">-</span>
            </td>
          </template>
          <template #entry_count="{ item }">
            <td class="text-center align-middle">{{ item.entry_count }}</td>
          </template>
          <template #routers="{ item }">
            <td class="align-middle">
              <small>{{ (item.routers || []).map(r => r.name).join('、') || '未关联' }}</small>
            </td>
          </template>
          <template #action="{ item }">
            <td class="align-middle">
              <CButtonGroup>
                <CButton color="primary" size="sm" @click="goDetail(item.id)">详情</CButton>
                <CButton color="success" size="sm" @click="syncGroup(item)">同步</CButton>
                <CButton color="info" size="sm" @click="openModal(item)">编辑</CButton>
                <CButton color="danger" size="sm" @click="deleteGroup(item)">删除</CButton>
              </CButtonGroup>
            </td>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>

    <!-- 组编辑 Modal -->
    <CModal :show.sync="modal.show" :title="modal.isEdit ? '编辑路由组' : '新建路由组'" color="primary">
      <CForm>
        <CInput label="组名称" v-model="modal.form.name" placeholder="如 fallback-ipv4" />
        <CRow>
          <CCol sm="4">
            <CSelect label="地址族" v-model="modal.form.family" :disabled="modal.isEdit" :options="[
              { value: 'ipv4', label: 'ipv4' },
              { value: 'ipv6', label: 'ipv6' }
            ]" />
          </CCol>
          <CCol sm="4">
            <CSelect label="路由类型" v-model="modal.form.route_type" :options="[
              { value: 'blackhole', label: 'blackhole（兜底汇总）' },
              { value: 'normal', label: 'normal' },
              { value: 'unreachable', label: 'unreachable' },
              { value: 'prohibit', label: 'prohibit' }
            ]" />
          </CCol>
          <CCol sm="4">
            <CInput label="Distance" v-model="modal.form.distance" placeholder="如 254" />
          </CCol>
        </CRow>
        <CInput label="Routing Table" v-model="modal.form.routing_table" placeholder="main" />
        <CInputCheckbox
          label="同时作为 address-list 下发（BGP 通告网段来源）"
          :checked="modal.form.as_address_list"
          @update:checked="v => modal.form.as_address_list = v"
          inline
        />
        <CInput
          v-if="modal.form.as_address_list"
          label="Address List 名称"
          v-model="modal.form.list_name"
          placeholder="如 address-list-ipv4-export-to-backbone"
          class="mt-2"
        />
        <CTextarea label="描述" v-model="modal.form.description" :rows="2" class="mt-2" />
        <CAlert color="info" fade>
          组级参数对组内所有网段生效。参数或条目修改后需在详情页点「同步」下发到关联路由器。
        </CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="modal.show = false">取消</CButton>
          <CButton color="success" :disabled="modal.loading" @click="submitItem">
            {{ modal.loading ? '保存中...' : '保存' }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 同步结果 Modal -->
    <CModal :show.sync="syncResultModal.show" title="同步结果" color="dark" size="lg">
      <table class="table table-sm">
        <thead>
          <tr>
            <th>路由器</th>
            <th>结果</th>
            <th>路由 +/-/set</th>
            <th v-if="syncResultModal.hasAlist">AList +/-</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in syncResultModal.results" :key="r.router_id">
            <td>{{ r.router_name }}</td>
            <td>
              <CBadge :color="r.ok ? 'success' : 'danger'">{{ r.ok ? '成功' : '失败' }}</CBadge>
              <small v-if="r.error" class="text-danger ml-1">{{ r.error }}</small>
            </td>
            <td v-if="r.ok">{{ r.route_added }} / {{ r.route_removed }} / {{ r.route_updated }}</td>
            <td v-else>-</td>
            <td v-if="syncResultModal.hasAlist">{{ r.ok ? (r.alist_added + ' / ' + r.alist_removed) : '-' }}</td>
          </tr>
        </tbody>
      </table>
      <template #footer>
        <CButton color="danger" @click="syncResultModal.show = false">关闭</CButton>
      </template>
    </CModal>

    <!-- 通用确认 Modal -->
    <CModal :show.sync="confirmModal.show" :title="confirmModal.title" color="primary">
      <p>{{ confirmModal.message }}</p>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="confirmModal.show = false">取消</CButton>
          <CButton color="success" @click="confirmModal.callback(); confirmModal.show = false">确认</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
export default {
  name: 'RouteGroupList',
  data() {
    return {
      loading: false,
      groups: [],
      fields: [
        { key: 'name', label: '组名称', _style: 'min-width:180px' },
        { key: 'family', label: '族/类型', _style: 'width:200px' },
        { key: 'alist', label: 'Address List', _style: 'width:200px' },
        { key: 'entry_count', label: '网段数', _style: 'width:80px' },
        { key: 'routers', label: '关联路由器', _style: 'min-width:200px' },
        { key: 'action', label: '操作', _style: 'width:230px' }
      ],
      modal: {
        show: false,
        isEdit: false,
        editId: null,
        loading: false,
        form: this.emptyForm()
      },
      syncResultModal: {
        show: false,
        results: [],
        hasAlist: false
      },
      confirmModal: {
        show: false, title: '确认', message: '', callback: () => {}
      }
    }
  },
  mounted() {
    this.loadList()
  },
  methods: {
    emptyForm() {
      return {
        name: '', family: 'ipv4', route_type: 'blackhole', distance: '254',
        routing_table: 'main', as_address_list: false, list_name: '', description: ''
      }
    },
    async loadList() {
      this.loading = true
      try {
        const res = await this.$axios.get('user/route-group/list')
        if (res.data.code === 200) {
          this.groups = res.data.data || []
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.loading = false
    },
    showConfirm(title, message, callback) {
      this.confirmModal.title = title
      this.confirmModal.message = message
      this.confirmModal.callback = callback
      this.confirmModal.show = true
    },
    goDetail(id) {
      this.$router.push('/routing/route-group/group/' + id)
    },
    openModal(g) {
      if (g) {
        this.modal.isEdit = true
        this.modal.editId = g.id
        this.modal.form = {
          name: g.name,
          family: g.family,
          route_type: g.route_type,
          distance: g.distance !== null && g.distance !== undefined ? String(g.distance) : '',
          routing_table: g.routing_table || 'main',
          as_address_list: !!g.as_address_list,
          list_name: g.list_name || '',
          description: g.description || ''
        }
      } else {
        this.modal.isEdit = false
        this.modal.editId = null
        this.modal.form = this.emptyForm()
      }
      this.modal.show = true
    },
    async submitItem() {
      const f = this.modal.form
      if (!f.name) {
        this.$toasted.show('请填写组名称', { type: 'error' })
        return
      }
      if (f.as_address_list && !f.list_name) {
        this.$toasted.show('启用 address-list 时必须填写 List 名称', { type: 'error' })
        return
      }
      this.modal.loading = true
      try {
        let res
        if (this.modal.isEdit) {
          res = await this.$axios.post(`user/route-group/${this.modal.editId}/edit`, f)
        } else {
          res = await this.$axios.post('user/route-group/add', f)
        }
        if (res.data.code === 200) {
          this.$toasted.show('已保存，参数变更需重新同步', { type: 'success' })
          this.modal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.modal.loading = false
    },
    syncGroup(g) {
      this.showConfirm('确认同步', `确认同步组 "${g.name}" 到 ${g.routers.length} 台关联路由器？`, async () => {
        try {
          const res = await this.$axios.post(`user/route-group/${g.id}/sync`)
          const results = (res.data.data || {}).results || []
          this.syncResultModal.results = results
          this.syncResultModal.hasAlist = !!g.as_address_list
          this.syncResultModal.show = true
          const failed = results.filter(r => !r.ok).length
          this.$toasted.show(
            failed > 0 ? `同步完成，${failed} 台失败` : '同步完成',
            { type: failed > 0 ? 'warning' : 'success' }
          )
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },
    deleteGroup(g) {
      if (g.routers && g.routers.length > 0) {
        this.$toasted.show('该组仍关联路由器，请先在详情页移除关联（会自动清理）', { type: 'error' })
        return
      }
      this.showConfirm('确认删除', `确认删除组 "${g.name}" 及其 ${g.entry_count} 条网段记录？`, async () => {
        try {
          const res = await this.$axios.delete(`user/route-group/${g.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已删除', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    }
  }
}
</script>
